import { useState, useEffect, useMemo, useRef } from 'react'
import Select, { components } from "react-select"
import RepAvatar from "../../Material/RepAvatar"
import { AllDriversQuery, RepsQuery, AllBoardsQuery, GroupsQuery } from "../Queries"
import { Query } from '@apollo/client/react/components'
import "./TypeAheadComponents.css"
import { useQuery, gql } from '@apollo/client'

export const GroupsTypeAhead = ({ onChange, defaultValue, returnOptions = false }) => {
    const [searchTerm, setSearchTerm] = useState("")
    const [prevSelectedMap, setPrevSelectedMap] = useState({})

    useEffect(() => {
        if (defaultValue && Array.isArray(defaultValue)) {
            const map = {}
            defaultValue.forEach(item => {
                if (typeof item === "string" || typeof item === "number") {
                    map[String(item)] = prevSelectedMap[item] || String(item)
                } else if (item?.value && item?.label) {
                    map[String(item.value)] = item.label
                }
            })
            setPrevSelectedMap(map)
        }
    }, [defaultValue])

    return (
        <Query query={GroupsQuery} variables={{ searchTerm }}>
            {({ loading, error, data }) => {
                if (loading) return <Select isLoading />
                if (error)
                    return (<Select isDisabledplaceholder={`Error! ${error.message}`} />)

                const options = data?.allGroups?.edges?.map((g) => ({
                    value: String(g.node.id),
                    label: g.node.name,
                })) || []

                if (!defaultValue) {
                    return (
                        <Select
                            isMulti
                            placeholder="Select Groups"
                            options={options}
                            defaultValue={undefined}
                            onInputChange={(val) => setSearchTerm(val)}
                            onChange={(sel) => onChange(returnOptions ? (sel || []) : (sel ? sel.map((g) => g.value) : []))}
                        />
                    )
                }

                const isIdArray = Array.isArray(defaultValue) && defaultValue.length > 0 && (typeof defaultValue[0] === "string" || typeof defaultValue[0] === "number")

                let controlledValue = null

                if (isIdArray) {
                    const normalizedIds = defaultValue.map(String)
                    const selectedInOptions = options.filter(opt => normalizedIds.includes(opt.value))
                    const missingSelected = normalizedIds.filter(id => !selectedInOptions.some(opt => opt.value === id)).map(id => ({ value: id, label: options.find(o => o.value === id)?.label || prevSelectedMap[id] || id }))
                    controlledValue = [...selectedInOptions, ...missingSelected]
                } else {
                    controlledValue = defaultValue.map(item => ({ value: String(item.value), label: item.label }))
                }
                return (
                    <Select
                        isMulti
                        placeholder="Select Groups"
                        options={options}
                        value={controlledValue}
                        onInputChange={(val) => setSearchTerm(val)}
                        onChange={(sel) =>
                            onChange(returnOptions ? (sel || []) : (sel ? sel.map((g) => g.value) : []))
                        }
                    />
                )
            }}
        </Query>
    )
}


export const BoardTypeAhead = ({ onChange, defaultValue, isDisabled = false }) => {
    const [searchTerm, setSearchTerm] = useState("")
    return (
        <Query query={AllBoardsQuery} variables={{ searchTerm }}>
            {({ loading, error, data }) => {
                if (loading) return <Select isLoading={true}></Select>
                if (error) return <Select isLoading={false} isDisabled={true} placeholder={`Error! ${error.message}`}></Select>
                let options = data && data.allBoards && data.allBoards.edges &&
                    data.allBoards.edges.map(board => ({
                        value: board.node.id,
                        label: board.node.title
                    }))

                let defaultOption = defaultValue && options?.find(option => option.value === defaultValue)
                return <Select
                    options={options}
                    isDisabled={isDisabled}
                    placeholder="Select Board"
                    onInputChange={(inputValue) => { setSearchTerm(inputValue) }}
                    onChange={(selected) => onChange(selected.value)}
                    defaultValue={defaultOption}
                    key="boardTypeAhead" // defaultvalue Won't work without this https://github.com/JedWatson/react-select/issues/5888
                />
            }}
        </Query>
    )
}

const DriverByIdQuery = gql`query DriverById($id: ID!){
    driver(id: $id){ id pk name branch{ id name } }
}`

export const DriverTypeAhead = ({ onChange, defaultValue, defaultLabelValue, isDisabled }) => {
    const [searchTerm, setSearchTerm] = useState("")
    const [selectedDriver, setSelectedDriver] = useState(null)

    const { loading, error, data } = useQuery(AllDriversQuery, {
        variables: searchTerm ? { searchTerm } : {}
    })

    const { data: singleDriverData } = useQuery(DriverByIdQuery, {
        variables: { id: defaultValue },
        skip: !defaultValue || !!selectedDriver,
        fetchPolicy: 'cache-first',
    })

    const options = useMemo(() => {
        return (
            data?.allDrivers?.edges?.map(driver => ({
                value: driver.node.id,
                label: `#${driver.node.pk} · ${driver.node.name} · ${driver.node.branch.name ?? ""}`
            })) || []
        )
    }, [data])

    // Resolve default selection from options list first
    useEffect(() => {
        if (selectedDriver) return

        if (defaultLabelValue) {
            setSelectedDriver(defaultLabelValue)
        } else if (defaultValue) {
            const match = options.find(o => o.value === defaultValue)
            if (match) setSelectedDriver(match)
        }
    }, [options, defaultValue, defaultLabelValue, selectedDriver])

    // Fall back to singular driver query when not found in initial options
    useEffect(() => {
        if (selectedDriver || !defaultValue || !singleDriverData?.driver) return
        const d = singleDriverData.driver
        setSelectedDriver({
            value: d.id,
            label: `#${d.pk} · ${d.name} · ${d.branch?.name ?? ""}`
        })
    }, [singleDriverData, defaultValue, selectedDriver])

    if (loading) return <Select isLoading />
    if (error)
        return (
            <Select
                isDisabled
                placeholder={`Error! ${error.message}`}
            />
        )

    return (
        <Select
            options={options}
            placeholder="Start typing to search for drivers..."
            value={selectedDriver}
            onInputChange={(inputValue, { action }) => {
                if (action === "input-change") {
                    setSearchTerm(inputValue)
                }
            }}
            onChange={(selected) => {
                setSelectedDriver(selected)
                onChange(selected.value)
            }}
        />
    )
}

const RepOption = (props) => (
    <components.Option {...props}>
        <div className="d-flex align-items-center">
            <RepAvatar rep={props.data.node} keepClose />
            <span className="ml-2">{props.label}</span>
        </div>
    </components.Option>
)

export const RepsTypeAhead = ({ onChange, defaultValue, group, permissions, returnObjects = false, allowedRepIds = null, extraRepOptions = [] }) => {
    const [searchTerm, setSearchTerm] = useState("")
    const [selectedReps, setSelectedReps] = useState(Array.isArray(defaultValue) ? defaultValue : [])
    const [queryVariables, setQueryVariables] = useState({ limit: 5, searchTerm: "", groups: group, permissions: permissions })
    const [loadedData, setLoadedData] = useState(null)
    const lastSyncedDefaultSignature = useRef(null)
    const hasDefaultValue = Array.isArray(defaultValue)
    const isArrayOfStrings = hasDefaultValue && defaultValue.every(id => typeof id === "string")
    const defaultSignature = useMemo(() => {
        if (!hasDefaultValue) {
            return null
        }
        return defaultValue
            .map((item) => {
                if (typeof item === "string") return item
                return item?.id || item?.value || ""
            })
            .join("|")
    }, [defaultValue, hasDefaultValue])

    const handleSelectChange = (node) => {
        if (!node) return
        if (!selectedReps.some(r => r.id === node.id)) {
            const updated = [...selectedReps, node]
            setSelectedReps(updated)
            onChange(returnObjects ? updated : updated.map(r => r.id))
        }
    }

    const handleRemoveRep = (repId) => {
        setSelectedReps(prevSelected => {
            const updated = prevSelected.filter(r => r.id !== repId)
            onChange(returnObjects ? updated : updated.length > 0 ? updated.map(r => r.id) : [])
            return updated
        })
    }

    const handleSearch = (inputValue) => {
        setSearchTerm(inputValue)
        setQueryVariables({
            limit: 5,
            searchTerm: inputValue && inputValue.trim().length > 0 ? inputValue.trim() : "",
            groups: group,
            permissions: permissions
        })
    }

    const shouldUseLocalOptionsOnly = Array.isArray(allowedRepIds)
        && allowedRepIds.length > 0
        && Array.isArray(extraRepOptions)
        && extraRepOptions.length > 0
        && (!group || group.length === 0)

    const buildLocalOptions = () => {
        const normalizedSearch = (searchTerm || "").trim().toLowerCase()
        return (extraRepOptions || [])
            .filter((rep) => rep?.id)
            .filter((rep) => !Array.isArray(allowedRepIds) || allowedRepIds.includes(rep.id))
            .filter((rep) => {
                if (!normalizedSearch) return true
                const label = rep.name || rep.username || `${rep.firstName || ""} ${rep.lastName || ""}`.trim()
                return [label, rep.username, rep.firstName, rep.lastName]
                    .filter(Boolean)
                    .some((value) => value.toLowerCase().includes(normalizedSearch))
            })
            .map((rep) => ({
                value: rep.id,
                label: rep.name || rep.username || `${rep.firstName || ""} ${rep.lastName || ""}`.trim(),
                node: rep,
            }))
    }

    useEffect(() => {
        setQueryVariables({ limit: 5, searchTerm: "", groups: group, permissions: permissions })
    }, [group, permissions])

    useEffect(() => {
        if (!hasDefaultValue) {
            return
        }

        if (defaultValue.length === 0) {
            setSelectedReps([])
            lastSyncedDefaultSignature.current = defaultSignature
            return
        }

        if (!isArrayOfStrings && lastSyncedDefaultSignature.current !== defaultSignature) {
            setSelectedReps(defaultValue)
            lastSyncedDefaultSignature.current = defaultSignature
        }
    }, [defaultValue, defaultSignature, hasDefaultValue, isArrayOfStrings])

    useEffect(() => {
        if (!hasDefaultValue) return
        if (!isArrayOfStrings) return

        if (lastSyncedDefaultSignature.current === defaultSignature) {
            return
        }

        if (shouldUseLocalOptionsOnly) {
            const repObjects = defaultValue
                .map(id => extraRepOptions.find(rep => rep.id === id))
                .filter(Boolean)

            if (repObjects.length !== defaultValue.length) {
                return
            }
            setSelectedReps(repObjects)
            lastSyncedDefaultSignature.current = defaultSignature
            return
        }
        if (!loadedData?.allReps?.edges || defaultValue.length === 0) {
            if (defaultValue.length === 0) {
                setSelectedReps([])
                lastSyncedDefaultSignature.current = defaultSignature
            }
            return
        }

        const repObjects = defaultValue
            .map(id => loadedData.allReps.edges.find(e => e.node.id === id)?.node || extraRepOptions.find(rep => rep.id === id))
            .filter(Boolean)

        if (repObjects.length !== defaultValue.length) {
            return
        }
        setSelectedReps(repObjects)
        lastSyncedDefaultSignature.current = defaultSignature
    }, [defaultValue, defaultSignature, loadedData, hasDefaultValue, isArrayOfStrings, extraRepOptions, shouldUseLocalOptionsOnly])

    return (
        <div className="reps-typeahead-container">
            {selectedReps.length > 0 && (
                <div className="selected-reps">
                    {selectedReps.map((rep) => (
                        <div key={rep.pk} className="selected-rep-chip">
                            <RepAvatar rep={rep} keepOpen />
                            <i className="fa fa-times remove-rep" onClick={() => handleRemoveRep(rep.id)} />
                        </div>
                    ))}
                </div>
            )}

            {shouldUseLocalOptionsOnly ? (
                <Select
                    options={buildLocalOptions()}
                    isLoading={false}
                    components={{ Option: RepOption }}
                    placeholder="Search and select reps..."
                    onInputChange={handleSearch}
                    onChange={(selected) => {
                        const matchedRep = extraRepOptions.find((rep) => rep.id === selected?.value)
                        handleSelectChange(matchedRep)
                    }}
                    isClearable={false}
                    value={null}
                    noOptionsMessage={() => searchTerm ? "No reps found for this search." : "No reps available."}
                />
            ) : (
                <Query query={RepsQuery} variables={queryVariables}>
                    {({ error, data, loading }) => {
                        if (data && data !== loadedData) {
                            setLoadedData(data)
                        }
                        if (error)
                            return (
                                <div className="text-danger text-center py-2">Error loading reps: {error.message}</div>
                            )
                        let allReps = data?.allReps?.edges?.map(rep => ({
                            value: rep.node.id, label: (rep.node.name || rep.node.username).trim(), node: rep.node,
                        })) || []

                        if (Array.isArray(allowedRepIds)) {
                            allReps = allReps.filter((rep) => allowedRepIds.includes(rep.value))
                        }

                        const normalizedExtraOptions = (extraRepOptions || [])
                            .filter((rep) => rep?.id)
                            .filter((rep) => !Array.isArray(allowedRepIds) || allowedRepIds.includes(rep.id))
                            .map((rep) => ({
                                value: rep.id,
                                label: rep.name || rep.username || `${rep.firstName || ""} ${rep.lastName || ""}`.trim(),
                                node: rep,
                            }))

                        const seenValues = new Set(allReps.map((rep) => rep.value))
                        normalizedExtraOptions.forEach((rep) => {
                            if (!seenValues.has(rep.value)) {
                                allReps.push(rep)
                                seenValues.add(rep.value)
                            }
                        })

                        const selectedIds = selectedReps.map(r => r.id || r.value)
                        allReps = [...allReps.filter(r => selectedIds.includes(r.value)), ...allReps.filter(r => !selectedIds.includes(r.value))]

                        return (
                            <Select options={allReps} isLoading={loading} components={{ Option: RepOption }} placeholder="Search and select reps..." onInputChange={handleSearch} onChange={(selected) => handleSelectChange(selected?.node)} isClearable={false} value={null} noOptionsMessage={() => searchTerm ? "No reps found for this search." : "No reps available."} />
                        )
                    }}
                </Query>
            )}
        </div>
    )
}
